import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToaster } from '../../../core/components/Toast.tsx';
import { CREATE_OUTLOOK_ADDIN } from '../../../customer/queries.ts';
import { AgentFormData } from '../components/config/ConfigSteps.tsx';
import { CREATE_AGENT, UPLOAD_AGENT_KNOWLEDGE_FILE } from '../queries.ts';
import { transformAgentFormData } from '../utils/transformAgentFormData';

export const useCreateAgent = () => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const { publishToast } = useToaster();
    const [createAgentMutation] = useMutation(CREATE_AGENT);
    const [uploadAgentKnowledgeFile] = useMutation(UPLOAD_AGENT_KNOWLEDGE_FILE);
    const [createOutlookAddIn] = useMutation(CREATE_OUTLOOK_ADDIN);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const createAgent = async (data: AgentFormData): Promise<string | null> => {
        setIsSubmitting(true);
        try {
            const { data: result } = await createAgentMutation({
                variables: { input: transformAgentFormData(data) },
            });
            const newAgent = result.createAgent.agent;

            await createOutlookAddIn({
                variables: {
                    name: newAgent.name,
                    channelMode: 'ADDIN',
                    orderChannels: [],
                    invoiceChannels: [],
                    orderConfirmationChannels: [],
                    rfqChannels: [],
                    deliveryNoteChannels: [],
                    agentId: newAgent.id,
                },
            });

            const filesToUpload = (data.files || []).filter((f: any): f is File => f instanceof File);
            for (const file of filesToUpload) {
                await uploadAgentKnowledgeFile({ variables: { agentId: newAgent.id, file } });
            }

            publishToast({ description: t('createModal.createSuccess'), status: 'success' });
            return newAgent.id;
        } catch (error) {
            console.error(error);
            publishToast({ description: t('createModal.createError'), status: 'error' });
            return null;
        } finally {
            setIsSubmitting(false);
        }
    };

    return { createAgent, isSubmitting };
};
